import { isDefined, isNotDefined } from '@sgme/fp';
import classNames from 'classnames';
import { VerticalFormGroup } from 'components/shared/FormGroup';
import { ErrorAlert } from 'components/shared/ScheduledRun/ErrorAlert';
import { ScheduledRunModalFooter } from 'components/shared/ScheduledRun/Modal/ScheduledRunModalFooter';
import { ScheduledRunModalHeader } from 'components/shared/ScheduledRun/Modal/ScheduledRunModalHeader';
import { ScheduleStatusModal } from 'components/shared/ScheduledRun/Modal/ScheduleStatusModal';
import type { IdOwnProps } from 'components/shared/ScheduledRun/type';
import { TextArea } from 'components/shared/TextField/TextArea';
import { useNavigateTo } from 'hooks/useNavigateTo';
import { Modal, ModalBody } from 'react-bootstrap';
import { FormattedMessage, useIntl } from 'react-intl';
import { connect, type MapDispatchToPropsFunction, type MapStateToProps } from 'react-redux';
import { scheduledRunActions } from 'state/scheduledRuns/actions';
import { hasSelectedRun } from 'state/scheduledRuns/selectors';
import type { ScheduledRunFields, SelectableRun } from 'state/scheduledRuns/state';
import type { AppState } from 'state/state.model';
import type { TypedError } from 'utils/errors/errors';
import { asError, asSuccess, isError, type LoadableContent } from 'utils/status';

import { DaysTriggerDisplay } from '../shared/DaysTriggerDisplay';
import { RangeTriggerDisplay } from '../shared/RangeTriggerDisplay';
import { ScheduleStatusBadge } from '../shared/ScheduleStatusBadge';

interface DispatchProps {
  onClosed: () => void;
  onQuoteAddRequested: () => void;
  onScheduleSelected: (runId: string) => void;
  onCommentChanged: (comment?: string) => void;
}
interface StateProps {
  runs: LoadableContent<readonly SelectableRun[], TypedError>;
  addRun: LoadableContent<undefined, TypedError>;

  comment?: string;
  hasSelectedRun: boolean;
}

interface Props extends DispatchProps, StateProps {}

const mapDispatchProps: MapDispatchToPropsFunction<DispatchProps, IdOwnProps> = (dispatch, { id }) => ({
  onClosed() {
    dispatch(scheduledRunActions.resetScheduleType(id));
  },
  onQuoteAddRequested() {
    dispatch(scheduledRunActions.addToScheduleRequested(id));
  },
  onCommentChanged(comment?: string) {
    dispatch(scheduledRunActions.commentChanged(id, comment));
  },
  onScheduleSelected(runId) {
    dispatch(scheduledRunActions.selectRun(id, runId));
  },
});
const modalId = 'add-to-schedule-modal';
const mapStateToProps: MapStateToProps<StateProps, IdOwnProps, AppState> = (state, { id }) => ({
  runs: state.scheduledRun.runs[id]?.runs,
  hasSelectedRun: hasSelectedRun(state, id),
  addRun: state.scheduledRun.runs[id].updateRun,
  comment: state.scheduledRun.runs[id].comment,
});

function AddToScheduleModal({
  hasSelectedRun,
  onClosed,
  onQuoteAddRequested,
  onScheduleSelected,
  onCommentChanged,
  comment,
  runs,
  addRun,
}: Props) {
  const navigateToScheduledQuotesBlotter = useNavigateTo('/scheduled-quotes-blotter');
  const { formatMessage } = useIntl();

  return addRun.status === 'success' ? (
    <ScheduleStatusModal
      title="scheduledRun.processDone.modal.addToExistingSchedule.title"
      icon="check"
      color="success"
      onClosed={onClosed}
      onLeftClicked={onClosed}
      onRightClicked={() => {
        onClosed();
        navigateToScheduledQuotesBlotter();
      }}
      rightBtn={'scheduledRun.processDone.modal.seeScheduledQuotes.button'}
    />
  ) : (
    <Modal onHide={onClosed} show id={modalId}>
      <ScheduledRunModalHeader
        title="scheduledRun.updateQuotes.modal"
        loadingStatus={addRun.status}
        onClosed={onClosed}
      />
      <ModalBody className="d-flex flex-column pb-2">
        <ul className="list-group list-group-flush overflow-y-auto border-top overflow-y-auto">
          {asSuccess(runs)?.content.map(run => (
            <ScheduledRunRow
              disabled={addRun.status === 'loading'}
              key={run.id}
              onSelected={() => onScheduleSelected(run.id)}
              {...run}
            />
          ))}
        </ul>
        {isError(runs) && <ErrorAlert {...runs.error} />}
        {isError(addRun) && <ErrorAlert {...addRun.error} />}
        <VerticalFormGroup<keyof ScheduledRunFields>
          inputId={'comment'}
          labelText={formatMessage({ id: 'scheduledRun.comment' })}
          errorPrefix=""
        >
          <TextArea
            disabled={addRun.status === 'loading'}
            value={comment}
            onValueChanged={onCommentChanged}
            className="form-control"
            placeholder={formatMessage({ id: 'scheduledRun.comment' })}
          />
        </VerticalFormGroup>
      </ModalBody>
      <ScheduledRunModalFooter
        modalId={modalId}
        error={asError(runs)?.error ?? asError(addRun)?.error}
        cancelBtn={
          <button
            className="btn btn-outline-secondary btn-outline btn-lg flex-grow-1 me-1  "
            data-e2e="add-to-scheduled-run-cancel-button"
            onClick={onClosed}
          >
            <FormattedMessage id="cancel" defaultMessage="Cancel" />
          </button>
        }
        validateBtn={
          <button
            disabled={!hasSelectedRun || runs.status === 'loading' || addRun.status === 'loading'}
            onClick={onQuoteAddRequested}
            className="btn btn-primary btn-lg flex-grow-1 ms-1 align-items-center "
            data-e2e="add-to-schedule-button"
          >
            <FormattedMessage id="add" />
          </button>
        }
        hasHelpBtn={isError(runs) || isError(addRun)}
      />
    </Modal>
  );
}
export const ConnectedAddToScheduleModal = connect(mapStateToProps, mapDispatchProps)(AddToScheduleModal);

interface ScheduledRunRowProps extends SelectableRun {
  onSelected: () => void;
  disabled: boolean;
}

function ScheduledRunRow({
  name,
  daysTrigger,
  rangeTrigger,
  status,
  onSelected,
  isSelected,
  disabled,
}: ScheduledRunRowProps) {
  return (
    <li
      className={classNames('list-group-item border-bottom d-flex align-items-center', { 'bg-lvl2': isSelected })}
      onClick={onSelected}
    >
      <div className="col-8">
        <div className="fw-bold">{name}</div>
        <div>
          {isDefined(daysTrigger) && isNotDefined(rangeTrigger) && (
            <DaysTriggerDisplay className="mt-1" {...daysTrigger} />
          )}
          {isDefined(rangeTrigger) && isNotDefined(daysTrigger) && (
            <RangeTriggerDisplay className="text-secondary" {...rangeTrigger} />
          )}
        </div>
      </div>
      <div className="col-3">
        {isDefined(status) && (
          <div className="d-flex justify-content-center">
            <ScheduleStatusBadge status={status} />
          </div>
        )}
      </div>
      <div className="col-1 text-center">
        <input
          disabled={disabled}
          className="form-check-input"
          type="radio"
          value=""
          name="add"
          checked={isSelected}
          onClick={onSelected}
        ></input>
      </div>
    </li>
  );
}
